import { useState, useEffect, useCallback, useMemo, useRef, type JSX, type ReactNode } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Copy,
  Check,
  Flag,
  ExternalLink,
  Package,
  Link,
  Database,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  Plus,
  X,
  RefreshCw,
  Info,
  AlertTriangle,
  AlertCircle,
  SlidersHorizontal,
  Layers,
  type LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';
import {
  useProductDetail,
  type ProductDetailRow,
  type ProductDetailProfitability,
} from '../../hooks/useCatalog';
import { useDebugPanel } from '../../hooks/useDebugPanel';
import { useSettingsConfig } from '../../hooks/useSettingsConfig';
import { useParamStore } from '../../hooks/useParamStore';
import { useApp } from '../../hooks/useApp';
import { supabase } from '../../lib/supabase';
import { PROVENANCE_SOURCE_MANUAL } from '../../lib/provenanceSources';
import { writeManualCost } from '../../lib/writeManualCost';
import { isRecallFallback } from '../../lib/resolveMatch';
import {
  soldCompsConfidenceTier,
  soldCompsWhy,
  soldCompsTierLabel,
} from '../../lib/soldCompsDisplay';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OverrideEditModal from '../../components/catalog/OverrideEditModal';
import ArbiSelect from '../../components/ui/ArbiSelect';
import { Toast, type ToastState } from '../../components/ui/Toast';
import ConcurrentEditBanner from '../../components/ui/ConcurrentEditBanner';
import { ProvenanceTooltip } from '../../components/ui/ProvenanceTooltip';
import {
  deriveProvenanceIndicator,
  sourcePrefix,
  type ProvenanceMap,
} from '../../components/ui/ProvenanceIndicator';
import type { PostgrestError } from '@supabase/supabase-js';
import { extractEnvelope } from '../../lib/extractEnvelope';
import { CONDITION_OPTIONS, conditionLabel } from '../../lib/conditions';

// Loose RPC caller — Supabase's typed rpc() expects known function names +
// param shapes from the generated types. scan_product_flags / upsert_variant_listing_link
// / remove_listing_link are called with dynamic args here; widen once at the
// module level rather than per-call.
type LooseRpc = (
  name: string,
  args?: Record<string, unknown>
) => PromiseLike<{ data: unknown; error: PostgrestError | null }>;
const callRpc = supabase.rpc.bind(supabase) as unknown as LooseRpc;

// ── Formatting ──────────────────────────────────────────────────────────────

function fmt$(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtPct(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `${Number(v).toFixed(1)}%`;
}
// W5b — the eBay sold-velocity summary cell (DISPLAY-ONLY; Correction-1 column, not engine-read).
// NULL-guarded (renders '—' until the velocity worker has populated the row). Surfaces the conservative
// confidence tier + the "why" caveats (window / recall-limited count / raw ceiling rows) as a tooltip.
function ebaySoldSummary(product: ProductDetailRow): ReactNode {
  if (product.ebay_sold_n_transactions == null) return '—';
  const sc = {
    n_transactions: product.ebay_sold_n_transactions ?? null,
    units_monthly: product.ebay_sold_units_monthly ?? null,
    window_days: product.ebay_sold_window_days ?? null,
    median_unit_price: product.ebay_sold_median_unit_price ?? null,
    median_landed_price: product.ebay_sold_median_landed_price ?? null,
    price_mix: product.ebay_sold_price_mix ?? null,
  };
  const tier = soldCompsConfidenceTier(sc);
  return (
    <span title={soldCompsWhy(sc).join(' · ')}>
      Sold:{' '}
      <span className="font-mono text-ink-primary">{product.ebay_sold_units_monthly ?? '—'}</span>
      /mo · {product.ebay_sold_n_transactions} txn
      {product.ebay_sold_median_unit_price != null && (
        <> · {fmt$(product.ebay_sold_median_unit_price)}/unit</>
      )}{' '}
      ·{' '}
      <span className={tier === 'medium' ? 'text-warning' : 'text-ink-muted'}>
        {soldCompsTierLabel(tier)}
      </span>{' '}
      <span className="text-ink-muted">(display-only)</span>
    </span>
  );
}
function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

// ── Resolve channel listing URL from product columns or algopix_data ────────

type Channel = 'amazon' | 'ebay' | 'walmart' | 'shopify';

interface ListingUrlConfig {
  column: string;
  algopixKeys: string[];
  fallback?: (product: ProductDetailRow) => string | null;
}

const LISTING_URL_KEYS: Record<Channel, ListingUrlConfig> = {
  amazon: {
    column: 'amazon_listing_url',
    algopixKeys: [
      'Amazon US Listing URL',
      'Amazon Listing URL',
      'Amazon URL',
      'amazon us listing url',
      'amazon listing url',
      'amazon url',
    ],
    fallback: (product) => (product.asin ? `https://www.amazon.com/dp/${product.asin}` : null),
  },
  ebay: {
    column: 'ebay_listing_url',
    algopixKeys: [
      'eBay US Listing URL',
      'eBay Listing URL',
      'eBay URL',
      'ebay us listing url',
      'ebay listing url',
      'ebay url',
    ],
  },
  walmart: {
    column: 'walmart_listing_url',
    algopixKeys: [
      'Walmart US Listing URL',
      'Walmart Listing URL',
      'Walmart URL',
      'walmart us listing url',
      'walmart listing url',
      'walmart url',
    ],
  },
  shopify: {
    column: 'shopify_listing_url',
    algopixKeys: [],
  },
};

function resolveListingUrl(
  product: ProductDetailRow,
  algopixData: Record<string, unknown>,
  channel: Channel
): string | null {
  const config = LISTING_URL_KEYS[channel];
  if (!config) return null;
  const colVal = product[config.column];
  if (typeof colVal === 'string' && colVal) return colVal;
  for (const key of config.algopixKeys) {
    const val = algopixData[key];
    if (typeof val === 'string' && val.startsWith('http')) return val;
  }
  if (config.fallback) return config.fallback(product);
  return null;
}

// ── Types for hook return + row shapes ──────────────────────────────────────
// (ProductDetailRow / ProductDetailProfitability / ProductDetailFlag /
//  ProductDetailImportInfo / ProductDetailListingLink are imported from the
//  useCatalog hook above.)

// Toast component + ToastState imported from src/components/ui/Toast.tsx (Day 81.5 P1.2 F3).

// ── Copy button ─────────────────────────────────────────────────────────────

interface CopyValueProps {
  value: string | null | undefined;
  mono?: boolean;
}

function CopyValue({ value, mono = true }: CopyValueProps): JSX.Element {
  const [copied, setCopied] = useState<boolean>(false);
  if (!value) return <span className="text-ink-muted">—</span>;
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className={clsx(
        'flex items-center gap-1 hover:text-accent transition-colors cursor-pointer',
        mono && 'font-mono'
      )}
      title="Click to copy"
    >
      {value}
      {copied ? (
        <Check size={10} className="text-success" />
      ) : (
        <Copy size={10} className="text-ink-muted" />
      )}
    </button>
  );
}

// ── Section wrapper ─────────────────────────────────────────────────────────

interface SectionProps {
  title: string;
  icon?: LucideIcon;
  children: ReactNode;
  defaultOpen?: boolean;
  action?: ReactNode;
}

function Section({
  title,
  icon: Icon,
  children,
  defaultOpen = true,
  action,
}: SectionProps): JSX.Element {
  const [open, setOpen] = useState<boolean>(defaultOpen);
  return (
    <div className="arbi-card overflow-hidden">
      <div className="flex items-center w-full px-4 py-2.5 bg-surface-3">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center flex-1 text-xs font-medium text-ink-secondary uppercase tracking-wider
                     hover:text-ink-primary transition-colors cursor-pointer select-none"
        >
          {open ? (
            <ChevronDown size={12} className="mr-2" />
          ) : (
            <ChevronRight size={12} className="mr-2" />
          )}
          {Icon && <Icon size={14} className="mr-1.5 text-ink-muted" />}
          {title}
        </button>
        {action && <div className="flex-shrink-0">{action}</div>}
      </div>
      {open && <div className="px-4 py-3">{children}</div>}
    </div>
  );
}

// ── Key-value row ───────────────────────────────────────────────────────────

interface KVProps {
  label: string;
  children: ReactNode;
}

function KV({ label, children }: KVProps): JSX.Element {
  return (
    <div className="flex items-start py-1.5">
      <span className="w-40 flex-shrink-0 text-xs text-ink-muted">{label}</span>
      <span className="text-xs text-ink-primary">{children}</span>
    </div>
  );
}

// ── Flag category badge ─────────────────────────────────────────────────────

const CATEGORY_COLORS: Record<string, string> = {
  unknown_brand: 'bg-warning/15 text-warning',
  no_market_price: 'bg-danger/15 text-danger',
  missing_price: 'bg-danger/15 text-danger',
  missing_demand: 'bg-warning/15 text-warning',
  missing_identifier: 'bg-surface-4 text-ink-muted',
  data_quality: 'bg-surface-4 text-ink-muted',
  no_amazon_listing: 'bg-info/15 text-info',
};
const CATEGORY_LABELS: Record<string, string> = {
  unknown_brand: 'Unknown Brand',
  no_market_price: 'No Match',
  missing_price: 'Missing Price',
  missing_demand: 'Missing Demand',
  missing_identifier: 'Missing ID',
  data_quality: 'Data Quality',
  no_amazon_listing: 'No Amazon Listing',
};

// W2b — match-candidate verdict badge colours (the scoreMatch verdict).
const VERDICT_COLORS: Record<string, string> = {
  trust: 'bg-success/15 text-success',
  suspect: 'bg-warning/15 text-warning',
  hold: 'bg-surface-4 text-ink-muted',
};

// W2b — a product_candidates row (the one-to-many resolver set); `source` drives the broad-match
// badge. W6.2: the recall cohort = any `*_fallback` source (spapi_catalog_fallback [Tier 2] /
// keepa_keyword_fallback [Tier 3 + legacy]) via isRecallFallback; spapi_catalog [Tier 1] is primary.
interface MatchCandidateRow {
  id: string;
  candidate_asin: string;
  title: string | null;
  brand: string | null;
  arbi_confidence_score: number | null;
  verdict: string | null;
  rank: number | null;
  source: string | null;
}

// W4a verification-gate render tuning. Cluster cue: when the top-2 candidate scores sit within this
// delta, flag a near-tie (rank alone isn't decisive — the 0.44-pt 6030 exemplar). Render cap: show
// the top-N by rank + a show-more toggle (all rows stay stored). Both tunable, not load-bearing.
const CLUSTER_SCORE_DELTA = 2.0;
const CANDIDATE_RENDER_CAP = 5;

// ── Profitability color ─────────────────────────────────────────────────────

function profitColor(row: ProductDetailProfitability | undefined | null): string {
  if (!row || row.no_price_data) return 'text-ink-muted';
  if (row.meets_min_roi) return 'text-success';
  if (row.is_profitable) return 'text-warning';
  return 'text-danger';
}
// U10 A1 W1 re-point — fpc path_key → display label (the fpc-seed mirror; Day 60b
// *_FALLBACK pattern). Keys are fulfillment-path keys, not channels.
const PATH_LABELS: Record<string, string> = {
  amazon_fba: 'FBA',
  amazon_fbm: 'FBM',
  ebay: 'eBay',
  walmart_self: 'WMT Self',
  walmart_wfs: 'WFS',
  shopify: 'Shopify',
};

// ── Channel options for listing links. listing_links.marketplace is a TEXT
// column holding these channel display names — a stored name OUTSIDE the
// public.marketplace enum family (does not rename at the enum's death). ──────

const CHANNEL_OPTIONS = [
  { value: 'Amazon', label: 'Amazon' },
  { value: 'eBay', label: 'eBay' },
  { value: 'Walmart', label: 'Walmart' },
  { value: 'Shopify', label: 'Shopify' },
];

// Copy URL helper — top-level function component so it isn't redeclared on
// every parent render (also keeps react-hooks/rules-of-hooks happy).
function CopyUrlButton({ url }: { url: string }): JSX.Element {
  const [copied, setCopied] = useState<boolean>(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="text-ink-muted hover:text-accent transition-colors cursor-pointer"
      title={copied ? 'Copied!' : 'Copy URL'}
      aria-label="Copy URL"
    >
      {copied ? <Check size={12} className="text-success" /> : <Copy size={12} />}
    </button>
  );
}

// ── Link form state ─────────────────────────────────────────────────────────

interface LinkForm {
  channel: string;
  url: string;
  listing_id: string;
  notes: string;
}

interface LinkErrors {
  channel?: string;
  url?: string;
}

// ── ProductDetail ───────────────────────────────────────────────────────────

export default function ProductDetail(): JSX.Element {
  const { isgSku } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    product,
    profitability,
    flags,
    loading,
    importInfo,
    importProductCount,
    listingLinks,
    velocityOverrides,
    priceOverrides,
    dismissFlag,
    refreshLinks,
    refreshFlags,
    refreshOverrides,
    refetch,
    variants,
    refreshVariants,
  } = useProductDetail(isgSku);

  // Day 47.5 — feed resolved product into Debug Panel context so Shift+Ctrl+D
  // auto-populates with the page's product. Fires once useProductDetail
  // resolves the SKU → product_id; no-op while loading or on a missing SKU.
  const { setSelectedProductId: setDebugProductId } = useDebugPanel();
  useEffect(() => {
    setDebugProductId(product?.id ?? null);
  }, [product?.id, setDebugProductId]);

  // Day 60b — derive active CHANNELS (4) from active PATHS (6) for the Algopix
  // listing-link fallback below. A channel is active if any of its paths is
  // active (amazon = amazon_fba OR amazon_fbm; walmart = walmart_self OR
  // walmart_wfs; ebay = ebay; shopify = shopify). Hardcoded mapping is fine
  // V1 — fulfillment_path_config.marketplace_id encodes the same relationship
  // server-side, but reading marketplaces table per-render is unwarranted
  // for a 4×2 derivation that only changes via enum migration.
  // Day 90 (CF 60) — marketplaces added for Marketplace Permissions picker
  // iteration. Picker mirrors Directory BrandForm L3015-L3043 button-toggle
  // group keyed by mkt.channel.
  const { activePathKeys, marketplaces } = useSettingsConfig();
  const activeChannels = useMemo<Set<Channel>>(() => {
    if (!activePathKeys || activePathKeys.length === 0) {
      return new Set<Channel>(['amazon', 'ebay', 'walmart', 'shopify']);
    }
    const result = new Set<Channel>();
    if (activePathKeys.includes('amazon_fba') || activePathKeys.includes('amazon_fbm')) {
      result.add('amazon');
    }
    if (activePathKeys.includes('ebay')) result.add('ebay');
    if (activePathKeys.includes('walmart_self') || activePathKeys.includes('walmart_wfs')) {
      result.add('walmart');
    }
    if (activePathKeys.includes('shopify')) result.add('shopify');
    return result;
  }, [activePathKeys]);

  const [dismissConfirm, setDismissConfirm] = useState<string | null>(null);
  const [removeLinkConfirm, setRemoveLinkConfirm] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  // W4a verification gate — Moment 1 (candidate confirm / reject-all).
  const [promoteConfirm, setPromoteConfirm] = useState<{
    candidateId: string;
    asin: string;
  } | null>(null);
  const [rejectAllConfirm, setRejectAllConfirm] = useState<boolean>(false);
  const [showAllCandidates, setShowAllCandidates] = useState<boolean>(false);
  const [gateBusy, setGateBusy] = useState<boolean>(false);
  // Day 90 (CF 60) — Marketplace Permissions picker draft state. Mirrors
  // BrandForm Directory.tsx L2880+ shape: Record<channel, boolean> initialized
  // from product.marketplace_permissions (?? {} for null/missing column).
  // Optimistic local-mutation pattern (set state → fire DB UPDATE → revert on
  // error). Admin-only edit per A8 amendment; section wholly hidden for
  // non-admin (no read-only display — engine-relevant policy class per
  // Architecture Rule 4).
  const [permsDraft, setPermsDraft] = useState<Record<string, boolean>>({});
  // Day 84 P6.3 L11 — OverrideEditModal target path (fpc path_key); null = closed.
  const [overrideModalPath, setOverrideModalPath] = useState<string | null>(null);
  // U2 S6 — the listing-link add-form is per-variant now; this holds the variant whose inline
  // form is open (null = closed). The write repoints to upsert_variant_listing_link(p_variant_id).
  const [addLinkVariantId, setAddLinkVariantId] = useState<string | null>(null);
  // U2 S6 — create-variant affordance state (condition picker over the nine canonical values).
  const [createVariantOpen, setCreateVariantOpen] = useState<boolean>(false);
  const [newVariantCondition, setNewVariantCondition] = useState<string>('');
  const [variantSaving, setVariantSaving] = useState<boolean>(false);
  const [variantError, setVariantError] = useState<string | null>(null);
  const [linkForm, setLinkForm] = useState<LinkForm>({
    channel: '',
    url: '',
    listing_id: '',
    notes: '',
  });
  const [linkErrors, setLinkErrors] = useState<LinkErrors>({});
  const [linkSaving, setLinkSaving] = useState<boolean>(false);
  const [rescanning, setRescanning] = useState<boolean>(false);

  // Assignee picker — admins+users only. Optimistic local state (override)
  // because useProductDetail doesn't expose a setProduct/refresh; on save
  // we update DB then setAssigneeOverride so the picker reflects the change
  // without a full reload. Sentinel `undefined` = "no edit yet, show
  // product.assigned_buyer_user_id".
  const [assigneeOptions, setAssigneeOptions] = useState<
    Array<{ id: string; full_name: string | null }>
  >([]);
  const [assigneeOverride, setAssigneeOverride] = useState<string | null | undefined>(undefined);
  const [assigneeSaving, setAssigneeSaving] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('profiles')
        .select('id, full_name, role, is_active')
        .in('role', ['admin', 'user'])
        .eq('is_active', true)
        .order('full_name');
      if (cancelled) return;
      setAssigneeOptions(
        ((data as { id: string; full_name: string | null }[] | null) ?? []).map((p) => ({
          id: p.id,
          full_name: p.full_name,
        }))
      );
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // W2b fetch → W4a gate: the pending-decision set (product_candidates status='candidate' only —
  // a decision queue, so once one is promoted (→'confirmed') or all are rejected the section clears).
  // Refactored to a callable so the gate (promote / reject-all) refetches it alongside refetch()
  // (product asin) + refreshFlags() (no_amazon_listing). `source` drives the broad-match badge.
  const [matchCandidates, setMatchCandidates] = useState<MatchCandidateRow[]>([]);
  const fetchCandidates = useCallback(async (): Promise<void> => {
    if (!product?.id) return;
    const { data } = await supabase
      .from('product_candidates')
      .select('id, candidate_asin, title, brand, arbi_confidence_score, verdict, rank, source')
      .eq('supplier_product_id', product.id)
      .eq('status', 'candidate')
      .order('rank', { ascending: true });
    setMatchCandidates((data as MatchCandidateRow[] | null) ?? []);
  }, [product?.id]);
  useEffect(() => {
    void fetchCandidates();
  }, [fetchCandidates]);

  const effectiveAssignee: string | null =
    assigneeOverride !== undefined
      ? assigneeOverride
      : ((product?.assigned_buyer_user_id as string | null | undefined) ?? null);

  async function handleAssigneeChange(newValue: string): Promise<void> {
    // ArbiSelect emits string. We use '' as the "Unassigned" sentinel.
    const next: string | null = newValue === '' ? null : newValue;
    if (!product?.id) return;
    setAssigneeSaving(true);
    const { error } = await supabase
      .from('products')
      .update({ assigned_buyer_user_id: next } as never)
      .eq('id', product.id);
    setAssigneeSaving(false);
    if (error) {
      showToast({ kind: 'error', message: `Assignee update failed — ${error.message}` });
      return;
    }
    setAssigneeOverride(next);
    showToast({
      kind: 'success',
      message: next ? 'Buyer assigned' : 'Buyer cleared',
    });
  }

  const showToast = useCallback((t: ToastState): void => setToast(t), []);

  // Day 90 (CF 60) — initialize permsDraft from product on load. Coerces
  // null/undefined column value to {} per BrandForm L2900 brandToDraft
  // precedent. Re-runs when product.id changes (different product loaded).
  useEffect(() => {
    const raw = product?.marketplace_permissions;
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      setPermsDraft(raw as Record<string, boolean>);
    } else {
      setPermsDraft({});
    }
  }, [product?.id, product?.marketplace_permissions]);

  // Day 90 (CF 60) — togglePerm handler. Admin-only (gated at section render
  // per A8 amendment; defensive isAdmin check here for direct-call safety).
  // Optimistic local mutation + DB UPDATE; revert on error per existing
  // ProductDetail handler patterns (cost edit, assignee change).
  async function togglePerm(channel: string): Promise<void> {
    if (!isAdmin || !product?.id) return;
    const prev = permsDraft;
    const next = { ...prev, [channel]: !prev[channel] };
    setPermsDraft(next); // optimistic
    const { error } = await supabase
      .from('products')
      .update({ marketplace_permissions: next })
      .eq('id', product.id);
    if (error) {
      setPermsDraft(prev); // revert
      showToast({
        kind: 'error',
        message: `Permission update failed — ${error.message}`,
      });
      return;
    }
    showToast({
      kind: 'success',
      message: `${channel} permission ${next[channel] ? 'allowed' : 'denied'}`,
    });
  }

  // ── Day 83 P6.2 — provenance UI + inline cost edit ───────────────────────
  // staleDays resolved once per page from manual_override_stale_days (90-day
  // business-level seed per Day 82 §2). 90 fallback if param hasn't loaded.
  const paramStore = useParamStore();
  const staleParam = paramStore.resolveKey('manual_override_stale_days');
  const staleParsed = Number(staleParam?.param.value);
  const staleDays: number = Number.isFinite(staleParsed) ? staleParsed : 90;

  // Role gate per Architecture Rule 4 — dist_rep doesn't see cost/profit data
  // anyway, but explicit gate for cost-edit affordance is belt-and-suspenders.
  // Day 90 (CF 60) — isAdmin added for Marketplace Permissions section gate per
  // A8 amendment + #DBN-7 (engine-relevant policy class; matches Settings cluster
  // pattern: Day 86 ChannelsAndPathsSection + Day 87 CostLineItemsSection +
  // Day 89 DomainScopeSection + Directory BrandsSection).
  const { isDistRep, user, isAdmin, isViewer } = useApp();

  // Optimistic local override pattern mirroring Day 55 assigneeOverride —
  // useProductDetail doesn't expose setProduct/refetch. After successful save,
  // setCostOverride + setProvenanceOverride drive the UI without round-tripping
  // the network. Sentinel `undefined` = "no edit yet, use product.*".
  const [costOverride, setCostOverride] = useState<number | null | undefined>(undefined);
  const [provenanceOverride, setProvenanceOverride] = useState<ProvenanceMap | null | undefined>(
    undefined
  );

  // U10 A1 — product.cost_usd is junction-assembled at useCatalog (primary
  // product_distributors row → cost_usd ?? null); survives the slice-B column drop.
  const effectiveCost: number | null =
    costOverride !== undefined ? costOverride : (product?.cost_usd ?? null);
  const effectiveProvenance: ProvenanceMap | null =
    provenanceOverride !== undefined
      ? provenanceOverride
      : ((product?.provenance as ProvenanceMap | null | undefined) ?? null);

  // W4a gate derivations. canConfirm: admin/user get the affordances; viewer is read-only (the
  // promote/reject RPCs 42501 viewer); dist_rep never sees candidates (product_candidates RLS).
  const canConfirm = !isViewer && !isDistRep;
  // U2 S6 — variant create + per-variant listing-link write require admin/user (variants RLS:
  // INSERT admin/user; viewer is read-only, dist_rep never reaches this page). Read stays open.
  const canManageVariants = !isViewer && !isDistRep;
  // Primary-source candidates above the broad-match (`*_fallback`) recall cohort (isRecallFallback),
  // each by rank; the cluster cue fires when the two highest scores sit within CLUSTER_SCORE_DELTA (a
  // soft signal — verify by image/listing — not a blocking state).
  const sortedCandidates = useMemo(() => {
    const fb = (c: MatchCandidateRow): number => (isRecallFallback(c.source) ? 1 : 0);
    return [...matchCandidates].sort(
      (a, b) => fb(a) - fb(b) || (a.rank ?? 9999) - (b.rank ?? 9999)
    );
  }, [matchCandidates]);
  const candidateClusterCue = useMemo(() => {
    const scores = matchCandidates
      .map((c) => c.arbi_confidence_score)
      .filter((s): s is number => typeof s === 'number')
      .sort((a, b) => b - a);
    return scores.length >= 2 && scores[0] - scores[1] <= CLUSTER_SCORE_DELTA;
  }, [matchCandidates]);
  const visibleCandidates = showAllCandidates
    ? sortedCandidates
    : sortedCandidates.slice(0, CANDIDATE_RENDER_CAP);

  // Inline cost-edit state — L5 single-field surface pattern (input + optional
  // reason textarea + Save/Cancel). Reason textarea placeholder per L5.
  const [editingCost, setEditingCost] = useState<boolean>(false);
  const [costInput, setCostInput] = useState<string>('');
  const [reasonInput, setReasonInput] = useState<string>('');
  const [costSaving, setCostSaving] = useState<boolean>(false);
  const costInputRef = useRef<HTMLInputElement | null>(null);

  // Focus the cost input on enter-edit per Settings.tsx InlineParamRow precedent
  // (avoid jsx-a11y/no-autofocus by using ref + effect instead of autoFocus).
  useEffect(() => {
    if (editingCost && costInputRef.current) costInputRef.current.focus();
  }, [editingCost]);

  function beginCostEdit(): void {
    setCostInput(effectiveCost !== null ? String(effectiveCost) : '');
    setReasonInput('');
    setEditingCost(true);
  }

  function handleCostCancel(): void {
    setEditingCost(false);
    setCostInput('');
    setReasonInput('');
  }

  async function handleCostSave(): Promise<void> {
    if (!product?.id) return;
    const trimmed = costInput.trim();
    // Null-cost save policy: BLOCK with toast (lean — Phase 3 Sol-adjudication
    // checkpoint). Cost is on the profitability math path; nullable assignee
    // (Day 55) is metadata-only — different semantic class.
    if (trimmed === '') {
      showToast({ kind: 'error', message: 'Enter a cost or click Cancel' });
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0) {
      showToast({ kind: 'error', message: 'Enter a valid non-negative number' });
      return;
    }
    // U6 S3 — the cost edit routes through write_junction_cost (the junction is
    // cost-canonical), which needs the product's distributor. distributor_id is nullable
    // and index-signature-typed on the wide ProductDetailRow — narrow to a concrete
    // string; without a distributor there is no junction row to write, so block early.
    const distributorId = product.distributor_id;
    if (typeof distributorId !== 'string' || distributorId === '') {
      showToast({
        kind: 'error',
        message: 'This product has no distributor — cost cannot be saved.',
      });
      return;
    }
    setCostSaving(true);
    // Optimistic local override (display only) — the authoritative provenance envelope
    // is written by write_junction_cost and read-copied to products.provenance.cost_usd
    // by the sync trigger; this guess (set_at/set_by/previous_value) is overwritten on
    // the next refetch (the Day 82 L5 split pattern).
    const newProv: ProvenanceMap = {
      ...((effectiveProvenance ?? {}) as ProvenanceMap),
      cost_usd: {
        source: PROVENANCE_SOURCE_MANUAL,
        reason: reasonInput.trim() || null,
        set_at: new Date().toISOString(),
        set_by: user?.id ?? null,
        previous_value: effectiveCost,
      },
    };
    // U6 S3 (C5-ff(i)) — route through write_junction_cost (the ONE junction-cost-write
    // boundary, C5), write_junction_cost's FIRST FE caller. The junction is canonical;
    // its AFTER trigger sync_products_primary_distributor read-copies cost_usd +
    // provenance.cost_usd → products, so the display (products.cost_usd) stays coherent.
    // This REPLACES the former direct products.cost_usd write (the KA3-F2 non-durability
    // hazard — a later junction sync silently reverted a products-only edit); the manual
    // cost is now the durable junction value. The products.cost_usd denorm column stays
    // (its DROP is the banked BACKLOG cost-denormalization-drop item, not this slice).
    const { error } = await writeManualCost(supabase, {
      productId: product.id,
      distributorId,
      cost: parsed,
      reason: reasonInput.trim() || null,
    });
    setCostSaving(false);
    if (error) {
      showToast({ kind: 'error', message: `Cost update failed — ${error.message}` });
      return;
    }
    setCostOverride(parsed);
    setProvenanceOverride(newProv);
    setEditingCost(false);
    setReasonInput('');
    showToast({ kind: 'success', message: 'Cost updated.' });
  }

  const costInd = deriveProvenanceIndicator(effectiveProvenance, 'cost_usd', staleDays);

  const isFromFlagged = location.pathname.startsWith('/catalog/flagged');
  const backLabel = isFromFlagged ? 'Back to Flagged' : 'Back to Products';
  const backPath = isFromFlagged ? '/catalog/flagged' : '/catalog/products';

  const openFlags = flags.filter((f) => f.status === 'open');

  // ── Listing link handlers ─────────────────────────────────

  // U2 S6 — create a variant (identity core). Direct INSERT (variants RLS: admin/user);
  // isg_sku auto-mints via next_isg_sku(). A duplicate (product_id, condition) trips the
  // UNIQUE constraint (23505) — surfaced inline, never a crash.
  async function handleCreateVariant(): Promise<void> {
    if (!product?.id || !newVariantCondition) {
      setVariantError('Pick a condition');
      return;
    }
    setVariantSaving(true);
    setVariantError(null);
    const { error } = await supabase
      .from('variants')
      .insert({ product_id: product.id, condition: newVariantCondition } as never);
    setVariantSaving(false);
    if (error) {
      setVariantError(
        error.code === '23505'
          ? `This product already has a ${conditionLabel(newVariantCondition)} variant.`
          : error.message
      );
      return;
    }
    showToast({
      kind: 'success',
      message: `Variant added — ${conditionLabel(newVariantCondition)}`,
    });
    setCreateVariantOpen(false);
    setNewVariantCondition('');
    await refreshVariants();
  }

  async function handleSaveLink(): Promise<void> {
    const errs: LinkErrors = {};
    if (!linkForm.channel) errs.channel = 'Required';
    if (!linkForm.url.trim()) errs.url = 'Required';
    else if (!linkForm.url.startsWith('http://') && !linkForm.url.startsWith('https://'))
      errs.url = 'Must start with http:// or https://';
    if (Object.keys(errs).length) {
      setLinkErrors(errs);
      return;
    }
    if (!product || !addLinkVariantId) return;

    setLinkSaving(true);
    // U2 S6 — the listing-link WRITE is variant-keyed now (upsert_variant_listing_link,
    // p_variant_id). Repoints the former product-keyed upsert_listing_link caller; the
    // Approach-F ambiguity shim + its guard retired with the rename (migration 000006).
    const rpcResult = await callRpc('upsert_variant_listing_link', {
      p_variant_id: addLinkVariantId,
      // p_marketplace = the DB fn's param name (stored name; renames only DB-side).
      // The value is a channel display name into listing_links.marketplace (TEXT).
      p_marketplace: linkForm.channel,
      p_url: linkForm.url.trim(),
      p_listing_id: linkForm.listing_id.trim() || null,
      p_notes: linkForm.notes.trim() || null,
    });
    setLinkSaving(false);
    const env = extractEnvelope<unknown>(rpcResult);
    if (!env.isOk) {
      showToast({ kind: 'error', message: env.userErrors[0]?.message ?? 'Save failed' });
      return;
    }
    showToast({ kind: 'success', message: 'Link saved' });
    setAddLinkVariantId(null);
    setLinkForm({ channel: '', url: '', listing_id: '', notes: '' });
    setLinkErrors({});
    refreshLinks();
  }

  async function handleRemoveLink(linkId: string): Promise<void> {
    const rpcResult = await callRpc('remove_listing_link', { p_link_id: linkId });
    const env = extractEnvelope<unknown>(rpcResult);
    if (!env.isOk) {
      showToast({ kind: 'error', message: env.userErrors[0]?.message ?? 'Remove failed' });
      return;
    }
    showToast({ kind: 'success', message: 'Link removed' });
    setRemoveLinkConfirm(null);
    refreshLinks();
  }

  // ── Re-scan flags ─────────────────────────────────────────

  async function handleRescan(): Promise<void> {
    if (!product?.id) return;
    setRescanning(true);
    const { error } = await callRpc('scan_product_flags', { p_product_id: product.id });
    if (error) {
      showToast({ kind: 'error', message: error.message });
      setRescanning(false);
      return;
    }
    await refreshFlags();
    const newOpen = flags.filter((f) => f.status === 'open').length;
    showToast({
      kind: 'success',
      message: `Scan complete — ${newOpen} flag${newOpen !== 1 ? 's' : ''} found`,
    });
    setRescanning(false);
  }

  // ── W4a verification gate — Moment 1 actions (mirror handleRescan: callRpc → toast → coordinated
  //    refetch: refetch() product asin · refreshFlags() no_amazon_listing · fetchCandidates() status).
  async function handlePromote(candidateId: string): Promise<void> {
    setGateBusy(true);
    const { error } = await callRpc('promote_match_candidate', { p_candidate_id: candidateId });
    if (error) {
      showToast({ kind: 'error', message: `Confirm failed — ${error.message}` });
      setGateBusy(false);
      return;
    }
    showToast({ kind: 'success', message: 'Match confirmed — product ASIN set' });
    refetch();
    await refreshFlags();
    await fetchCandidates();
    setGateBusy(false);
  }
  async function handleRejectAll(): Promise<void> {
    if (!product?.id) return;
    setGateBusy(true);
    const { error } = await callRpc('reject_all_candidates', { p_product_id: product.id });
    if (error) {
      showToast({ kind: 'error', message: `Reject failed — ${error.message}` });
      setGateBusy(false);
      return;
    }
    showToast({ kind: 'success', message: 'All candidates rejected — flagged no Amazon listing' });
    refetch();
    await refreshFlags();
    await fetchCandidates();
    setGateBusy(false);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <span className="text-sm text-ink-muted">Loading product...</span>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3">
        <Package size={24} className="text-ink-muted opacity-40" />
        <p className="text-sm text-ink-muted">Product not found</p>
        <button
          onClick={() => navigate(backPath)}
          className="text-xs text-accent hover:text-accent-hover"
        >
          {backLabel}
        </button>
      </div>
    );
  }

  const algopixData = (product.algopix_data ?? {}) as Record<string, unknown>;
  // W4c — Amazon-first for resolved products: the facts-worker's amazon_image_url, then the import
  // image (image_url, often Walmart), then the algopix_data fallback. Unresolved rows have
  // amazon_image_url null → the chain is unchanged (no regression).
  const rawImageUrl = product.amazon_image_url || product.image_url || algopixData.image_url;
  const imageUrl = typeof rawImageUrl === 'string' ? rawImageUrl : null;

  // Build Algopix fallback links (only for channels without a manual link
  // AND channels that are still active per fulfillment_path_config — Day 60b).
  // l.marketplace = listing_links.marketplace, a TEXT column of channel display
  // names (stored name outside the enum family; renames only if the column does).
  const manualChannels = new Set(listingLinks.map((l) => l.marketplace.toLowerCase()));
  const algopixFallbacks: { channel: Channel; label: string; url: string }[] = [];
  const channels: [Channel, string][] = [
    ['amazon', 'Amazon'],
    ['ebay', 'eBay'],
    ['walmart', 'Walmart'],
    ['shopify', 'Shopify'],
  ];
  for (const [channel, label] of channels) {
    if (!activeChannels.has(channel)) continue;
    if (!manualChannels.has(label.toLowerCase())) {
      const url = resolveListingUrl(product, algopixData, channel);
      if (url) algopixFallbacks.push({ channel, label, url });
    }
  }

  return (
    <div className="h-full overflow-auto">
      <Toast toast={toast} onDismiss={() => setToast(null)} />
      <div className="max-w-5xl mx-auto p-5 space-y-4">
        {/* Day 98.5b — concurrent-edit banner (another user's UPDATE → refresh) */}
        <ConcurrentEditBanner table="products" recordId={product.id} onRefresh={refetch} />
        {/* Back + Header */}
        <div>
          {/* Day 88.5 P3 — Back nav + Override-params deep-link as page-header
              actions (Q4 site (c)). Override button hidden for dist_rep per
              ProductDetail role-gating convention (L1329 precedent: !isDistRep
              gates the Edit overrides table button). */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => navigate(backPath)}
              className="flex items-center gap-1 text-xs text-ink-muted hover:text-ink-primary transition-colors cursor-pointer"
            >
              <ArrowLeft size={12} /> {backLabel}
            </button>
            {!isDistRep && (
              <button
                type="button"
                onClick={() =>
                  navigate(`/settings?mode=product&entity_id=${product.id}#section-params`)
                }
                className="flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink-primary transition-colors cursor-pointer"
                aria-label="Override engine parameters for this product"
              >
                <SlidersHorizontal size={12} /> Override params for this product →
              </button>
            )}
          </div>

          <div className="flex items-start gap-4">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt=""
                className="w-20 h-20 rounded-lg border border-border object-contain bg-white flex-shrink-0"
              />
            ) : (
              <div className="w-20 h-20 rounded-lg border border-border bg-surface-3 flex items-center justify-center flex-shrink-0">
                <Package size={24} className="text-ink-muted" />
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-mono text-accent">{product.isg_sku}</span>
                {openFlags.length > 0 && (
                  <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-danger/15 text-danger text-2xs font-medium">
                    <Flag size={9} /> {openFlags.length} flag{openFlags.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <h1 className="text-xl font-semibold text-ink-bright mt-1">{product.title || '—'}</h1>
              <p className="text-sm text-ink-secondary">{product.brand || '—'}</p>
              {/* W6.1 — SP-API risk chips (display-only; NULL=unchecked shows nothing). Gated = a hard
                  sell-block (danger); Hazmat = a shipping/FBA caution (warning). U2 S6 — the former
                  clearance-lot chip is REMOVED (lots-as-products model retired, adjudication §5;
                  clearance-lot FE returns at owning units against acquisition events). */}
              {(product.is_gated === true || product.is_hazmat === true) && (
                <div className="flex items-center gap-1.5 mt-1.5">
                  {product.is_gated === true && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-danger/15 text-danger text-xs font-medium px-2 py-0.5"
                      title="SP-API Listings Restrictions: account-gated for this ASIN (new) — eligibility approval required to sell."
                    >
                      <AlertTriangle size={11} /> Gated
                    </span>
                  )}
                  {product.is_hazmat === true && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-warning/15 text-warning text-xs font-medium px-2 py-0.5"
                      title="SP-API Catalog: dangerous goods (hazmat) — shipping / FBA restrictions may apply."
                    >
                      <AlertTriangle size={11} /> Hazmat
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section 1 — Identifiers */}
        <Section title="Identifiers">
          <div className="grid grid-cols-2 gap-x-8">
            <KV label="ISG SKU">
              <CopyValue value={product.isg_sku} />
            </KV>
            <KV label="UPC">
              <CopyValue value={product.upc} />
            </KV>
            <KV label="ASIN">
              <CopyValue value={product.asin} />
            </KV>
            <KV label="EAN">
              <CopyValue
                value={
                  product.ean || (typeof algopixData.ean === 'string' ? algopixData.ean : null)
                }
              />
            </KV>
            <KV label="MPN">
              <CopyValue value={product.mpn} />
            </KV>
            <KV label="Model">
              <CopyValue value={product.model} />
            </KV>
            <KV label="Supplier Part">
              <CopyValue value={product.supplier_part_no} />
            </KV>
            <KV label="Brand">
              <span className="text-ink-primary">{product.brand || '—'}</span>
            </KV>
            <KV label="Category">
              <span className="text-ink-primary">{product.category_algopix || '—'}</span>
            </KV>
            <KV label="Condition">
              <span className="text-ink-primary">
                {typeof algopixData.condition === 'string' ? algopixData.condition : 'New'}
              </span>
            </KV>
            <KV label="Buyer">
              <div className="-my-1">
                <ArbiSelect
                  value={effectiveAssignee ?? ''}
                  options={[
                    { value: '', label: 'Unassigned' },
                    ...assigneeOptions.map((p) => ({
                      value: p.id,
                      label: p.full_name ?? '(unnamed)',
                    })),
                  ]}
                  onChange={(v) => handleAssigneeChange(v)}
                  placeholder="Unassigned"
                  size="xs"
                  disabled={assigneeSaving}
                />
              </div>
            </KV>
          </div>
        </Section>

        {/* Section — Variants (U2 S6 · Identity Core). Each variant = (condition · isg_sku); the
            per-variant "Add link" action writes via upsert_variant_listing_link (the repointed
            :763 caller). Create-variant picks from the nine canonical conditions; a duplicate
            (product, condition) surfaces the UNIQUE violation inline (no crash). This panel is the
            conceptual replacement for the removed lots-as-products model (adjudication §5). */}
        <Section
          title="Variants"
          icon={Layers}
          action={
            canManageVariants ? (
              <button
                onClick={() => {
                  setCreateVariantOpen((v) => !v);
                  setNewVariantCondition('');
                  setVariantError(null);
                }}
                className="arbi-btn-ghost text-xs px-2 py-1"
              >
                <Plus size={12} /> Add Variant
              </button>
            ) : undefined
          }
        >
          {/* Create-variant form */}
          {canManageVariants && createVariantOpen && (
            <div className="mb-4 p-3 bg-surface-2 rounded-lg space-y-3">
              <div>
                <label
                  htmlFor="pd-variant-condition"
                  className="block text-xs font-medium text-ink-secondary mb-1"
                >
                  Condition <span className="text-danger">*</span>
                </label>
                <div id="pd-variant-condition" className="w-72 max-w-full">
                  <ArbiSelect
                    value={newVariantCondition}
                    options={CONDITION_OPTIONS}
                    onChange={(v) => {
                      setNewVariantCondition(v);
                      setVariantError(null);
                    }}
                    placeholder="Select condition..."
                    className="w-full"
                  />
                </div>
                {variantError && <p className="text-xs text-danger mt-1">{variantError}</p>}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCreateVariant}
                  disabled={variantSaving}
                  className="arbi-btn-primary text-xs px-3 py-1.5"
                >
                  {variantSaving ? 'Creating…' : 'Create Variant'}
                </button>
                <button
                  onClick={() => {
                    setCreateVariantOpen(false);
                    setNewVariantCondition('');
                    setVariantError(null);
                  }}
                  className="arbi-btn-ghost text-xs px-3 py-1.5"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Variant rows */}
          {variants.length > 0 ? (
            <div className="space-y-1.5">
              {variants.map((v) => (
                <div key={v.id}>
                  <div className="flex items-center gap-3 py-1.5 border-b border-border-subtle">
                    <span className="w-44 flex-shrink-0 px-1.5 py-0.5 rounded text-2xs font-medium bg-surface-2 text-ink-secondary">
                      {conditionLabel(v.condition)}
                    </span>
                    <span className="flex-1 min-w-0 text-xs">
                      <CopyValue value={v.isg_sku} />
                    </span>
                    {canManageVariants && (
                      <button
                        onClick={() => {
                          setAddLinkVariantId((cur) => (cur === v.id ? null : v.id));
                          setLinkForm({ channel: '', url: '', listing_id: '', notes: '' });
                          setLinkErrors({});
                        }}
                        className="arbi-btn-ghost text-2xs px-2 py-0.5 flex-shrink-0"
                        title="Add a listing link for this variant"
                      >
                        <Link size={11} /> Add link
                      </button>
                    )}
                  </div>

                  {/* Per-variant add-link form (U2 S6 — upsert_variant_listing_link(p_variant_id)) */}
                  {canManageVariants && addLinkVariantId === v.id && (
                    <div className="my-2 ml-2 p-3 bg-surface-2 rounded-lg space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label
                            htmlFor="pd-link-channel"
                            className="block text-xs font-medium text-ink-secondary mb-1"
                          >
                            Channel <span className="text-danger">*</span>
                          </label>
                          <div id="pd-link-channel">
                            <ArbiSelect
                              value={linkForm.channel}
                              options={CHANNEL_OPTIONS}
                              onChange={(val) => setLinkForm((f) => ({ ...f, channel: val }))}
                              placeholder="Select..."
                              className="w-full"
                            />
                          </div>
                          {linkErrors.channel && (
                            <p className="text-xs text-danger mt-0.5">{linkErrors.channel}</p>
                          )}
                        </div>
                        <div>
                          <label
                            htmlFor="pd-link-listing-id"
                            className="block text-xs font-medium text-ink-secondary mb-1"
                          >
                            Listing ID
                          </label>
                          <input
                            id="pd-link-listing-id"
                            value={linkForm.listing_id}
                            onChange={(e) =>
                              setLinkForm((f) => ({ ...f, listing_id: e.target.value }))
                            }
                            className="arbi-input w-full text-xs"
                            placeholder="ASIN / Item ID / SKU"
                          />
                        </div>
                      </div>
                      <div>
                        <label
                          htmlFor="pd-link-url"
                          className="block text-xs font-medium text-ink-secondary mb-1"
                        >
                          URL <span className="text-danger">*</span>
                        </label>
                        <input
                          id="pd-link-url"
                          value={linkForm.url}
                          onChange={(e) => setLinkForm((f) => ({ ...f, url: e.target.value }))}
                          className="arbi-input w-full text-xs"
                          placeholder="https://www.amazon.com/dp/..."
                        />
                        {linkErrors.url && (
                          <p className="text-xs text-danger mt-0.5">{linkErrors.url}</p>
                        )}
                      </div>
                      <div>
                        <label
                          htmlFor="pd-link-notes"
                          className="block text-xs font-medium text-ink-secondary mb-1"
                        >
                          Notes
                        </label>
                        <input
                          id="pd-link-notes"
                          value={linkForm.notes}
                          onChange={(e) => setLinkForm((f) => ({ ...f, notes: e.target.value }))}
                          className="arbi-input w-full text-xs"
                          placeholder="Optional notes"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveLink}
                          disabled={linkSaving}
                          className="arbi-btn-primary text-xs px-3 py-1.5"
                        >
                          {linkSaving ? 'Saving...' : 'Save Link'}
                        </button>
                        <button
                          onClick={() => {
                            setAddLinkVariantId(null);
                            setLinkErrors({});
                          }}
                          className="arbi-btn-ghost text-xs px-3 py-1.5"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-xs text-ink-muted mb-2">No variants yet</p>
              {canManageVariants && (
                <button
                  onClick={() => {
                    setCreateVariantOpen(true);
                    setNewVariantCondition('');
                    setVariantError(null);
                  }}
                  className="arbi-btn-ghost text-xs px-3 py-1.5"
                >
                  <Plus size={12} /> Add the first variant
                </button>
              )}
            </div>
          )}
        </Section>

        {/* Section 2 — Data Lineage */}
        <Section title="Data Lineage" icon={Database}>
          <div className="grid grid-cols-2 gap-x-8">
            <KV label="Imported From">
              {importInfo?.file_name ? (
                <span
                  className="text-ink-primary truncate max-w-xs inline-block"
                  title={importInfo.file_name}
                >
                  {importInfo.file_name.length > 40
                    ? importInfo.file_name.substring(0, 40) + '…'
                    : importInfo.file_name}
                </span>
              ) : (
                <span className="text-ink-muted">—</span>
              )}
            </KV>
            <KV label="Import Date">
              <span className="font-mono text-ink-secondary">
                {importInfo?.created_at
                  ? new Date(importInfo.created_at).toLocaleDateString()
                  : '—'}
              </span>
            </KV>
            <KV label="Source Row">
              <span className="font-mono text-ink-secondary">
                {product.source_row_number || '—'}
              </span>
            </KV>
            <KV label="Products in Import">
              <span className="font-mono text-ink-secondary">
                {importProductCount !== null ? importProductCount.toLocaleString() : '—'}
              </span>
            </KV>
          </div>
          {!product.import_id && (
            <p className="text-2xs text-ink-muted mt-2">
              Import source not tracked for this product.
            </p>
          )}
        </Section>

        {/* Section 3 — Physical */}
        <Section title="Physical">
          <div className="grid grid-cols-2 gap-x-8">
            <KV label="Weight">{product.weight_lbs ? `${product.weight_lbs} lbs` : '—'}</KV>
            <KV label="Dimensions">
              {product.length_in && product.width_in && product.height_in
                ? `${product.length_in}" × ${product.width_in}" × ${product.height_in}"`
                : '—'}
            </KV>
            <KV label="Color">{product.color || '—'}</KV>
            <KV label="Image">
              {imageUrl ? (
                <a
                  href={imageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:text-accent-hover flex items-center gap-1"
                >
                  View <ExternalLink size={10} />
                </a>
              ) : (
                '—'
              )}
            </KV>
          </div>
        </Section>

        {/* Section 4 — Listing Links. U2 S6: a product-level aggregate READ across the product's
            variants (get_listing_links — S5-proven dist_rep filtering). The WRITE moved to the
            per-variant "Add link" action in the Variants panel; this section is read + remove only. */}
        <Section title="Listing Links" icon={Link}>
          {/* Manual links */}
          {listingLinks.length > 0 && (
            <div className="space-y-1.5">
              {listingLinks.map((link) => {
                const statusColors: Record<string, string> = {
                  active: 'bg-success/15 text-success',
                  pending: 'bg-warning/15 text-warning',
                  suppressed: 'bg-danger/15 text-danger',
                  inactive: 'bg-surface-4 text-ink-muted',
                };
                return (
                  <div
                    key={link.id}
                    className="flex items-center gap-3 py-1.5 border-b border-border-subtle"
                  >
                    {/* link.marketplace = listing_links.marketplace (TEXT channel
                        display value; stored name outside the enum family). */}
                    <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-surface-2 text-ink-secondary flex-shrink-0">
                      {link.marketplace}
                    </span>
                    {link.status && (
                      <span
                        className={clsx(
                          'px-1.5 py-0.5 rounded text-2xs font-medium flex-shrink-0',
                          statusColors[link.status] || 'bg-surface-4 text-ink-muted'
                        )}
                      >
                        {link.status}
                      </span>
                    )}
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-accent hover:text-accent-hover truncate flex-1"
                      title={link.url}
                    >
                      {link.url.length > 50 ? link.url.substring(0, 50) + '…' : link.url}
                    </a>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-ink-muted hover:text-accent transition-colors flex-shrink-0"
                      title="Open listing"
                      aria-label="Open listing"
                    >
                      <ExternalLink size={12} />
                    </a>
                    <CopyUrlButton url={link.url} />
                    <button
                      onClick={() => setRemoveLinkConfirm(link.id)}
                      className="text-ink-muted hover:text-danger transition-colors cursor-pointer"
                      title="Remove link"
                      aria-label="Remove link"
                    >
                      <X size={12} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Algopix fallbacks */}
          {algopixFallbacks.length > 0 && (
            <div className={clsx('space-y-1.5', listingLinks.length > 0 && 'mt-2')}>
              {algopixFallbacks.map((fb) => (
                <div key={fb.channel} className="flex items-center gap-3 py-1.5 opacity-60">
                  <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-surface-2 text-ink-muted flex-shrink-0">
                    {fb.label}
                  </span>
                  <a
                    href={fb.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-ink-muted hover:text-accent truncate flex-1"
                    title={fb.url}
                  >
                    {fb.url.length > 50 ? fb.url.substring(0, 50) + '…' : fb.url}
                  </a>
                  <span className="text-2xs text-ink-muted flex-shrink-0">Algopix (fallback)</span>
                  <CopyUrlButton url={fb.url} />
                </div>
              ))}
            </div>
          )}

          {/* Empty state */}
          {listingLinks.length === 0 && algopixFallbacks.length === 0 && (
            <div className="text-center py-4">
              <p className="text-xs text-ink-muted">
                No listing links yet — add one from a variant above.
              </p>
            </div>
          )}
        </Section>

        {/* Section 5 — Pricing & Channel Data */}
        <Section title="Pricing & Channel Data">
          <div className="mb-3">
            <KV label="Product Cost">
              {editingCost ? (
                <div className="space-y-2 w-72">
                  <input
                    ref={costInputRef}
                    data-testid="cost-input"
                    type="text"
                    inputMode="decimal"
                    value={costInput}
                    onChange={(e) => setCostInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCostSave();
                      }
                      if (e.key === 'Escape') handleCostCancel();
                    }}
                    className="arbi-input w-full text-xs font-mono"
                    placeholder="0.00"
                  />
                  <textarea
                    data-testid="cost-reason"
                    value={reasonInput}
                    onChange={(e) => setReasonInput(e.target.value)}
                    className="arbi-input w-full text-xs"
                    rows={2}
                    placeholder="Why this change? (optional)"
                  />
                  <div className="flex gap-1 justify-end">
                    <button
                      onClick={handleCostSave}
                      disabled={costSaving}
                      data-testid="cost-save"
                      className="arbi-btn-primary text-xs px-2 py-1"
                    >
                      <Check size={11} /> {costSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      onClick={handleCostCancel}
                      className="arbi-btn-ghost text-xs px-2 py-1"
                      aria-label="Cancel"
                    >
                      <X size={11} />
                    </button>
                  </div>
                </div>
              ) : (
                <ProvenanceTooltip
                  provenance={effectiveProvenance}
                  fieldKey="cost_usd"
                  staleThresholdDays={staleDays}
                  mode="inline"
                  previousValueFormatter={(v) => fmt$(typeof v === 'number' ? v : null)}
                >
                  {isDistRep ? (
                    <span
                      className={clsx(
                        'font-mono',
                        costInd.stale ? 'text-ink-muted' : 'text-ink-bright'
                      )}
                    >
                      {fmt$(effectiveCost)}
                    </span>
                  ) : (
                    <button
                      onClick={beginCostEdit}
                      data-testid="cost-edit-trigger"
                      className={clsx(
                        'font-mono hover:text-accent transition-colors cursor-pointer',
                        costInd.stale ? 'text-ink-muted' : 'text-ink-bright'
                      )}
                    >
                      {fmt$(effectiveCost)}
                    </button>
                  )}
                </ProvenanceTooltip>
              )}
            </KV>
            <KV label="MSRP">
              <span className="font-mono">{fmt$(product.msrp)}</span>
            </KV>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-ink-muted border-b border-border-subtle">
                <th className="px-2 py-1.5 text-left font-medium">Channel</th>
                <th className="px-2 py-1.5 text-left font-medium">Price</th>
                <th className="px-2 py-1.5 text-left font-medium">Demand</th>
                <th className="px-2 py-1.5 text-left font-medium">Mo. Sales</th>
                <th className="px-2 py-1.5 text-left font-medium">Sellers</th>
                <th className="px-2 py-1.5 text-left font-medium">By Amazon</th>
                <th className="px-2 py-1.5 text-left font-medium">Rec.</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-border-subtle">
                <td className="px-2 py-1.5 font-medium text-ink-primary">
                  <span className="flex items-center gap-1">
                    Amazon
                    {(product.asin || resolveListingUrl(product, algopixData, 'amazon')) && (
                      <a
                        href={
                          resolveListingUrl(product, algopixData, 'amazon') ||
                          `https://www.amazon.com/dp/${product.asin}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent hover:text-accent-hover"
                        title="View listing"
                      >
                        <ExternalLink size={10} />
                      </a>
                    )}
                  </span>
                </td>
                <td className="px-2 py-1.5 font-mono">{fmt$(product.amazon_buy_box_price)}</td>
                <td className="px-2 py-1.5">{product.amazon_demand_rating || '—'}</td>
                <td className="px-2 py-1.5 font-mono">{product.amazon_monthly_sales ?? '—'}</td>
                <td className="px-2 py-1.5 font-mono">{product.amazon_seller_count ?? '—'}</td>
                <td className="px-2 py-1.5">
                  {product.amazon_offered_by_amazon === true
                    ? 'Yes'
                    : product.amazon_offered_by_amazon === false
                      ? 'No'
                      : '—'}
                </td>
                <td className="px-2 py-1.5 text-ink-secondary">
                  {product.algopix_recommendation || '—'}
                </td>
              </tr>
              <tr className="border-b border-border-subtle">
                <td className="px-2 py-1.5 font-medium text-ink-primary">
                  <span className="flex items-center gap-1">
                    eBay
                    {resolveListingUrl(product, algopixData, 'ebay') && (
                      <a
                        href={resolveListingUrl(product, algopixData, 'ebay') ?? undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent hover:text-accent-hover"
                        title="View listing"
                      >
                        <ExternalLink size={10} />
                      </a>
                    )}
                  </span>
                </td>
                <td className="px-2 py-1.5 font-mono">{fmt$(product.ebay_price)}</td>
                <td className="px-2 py-1.5 text-ink-secondary" colSpan={5}>
                  {ebaySoldSummary(product)}
                </td>
              </tr>
              <tr className="border-b border-border-subtle">
                <td className="px-2 py-1.5 font-medium text-ink-primary">
                  <span className="flex items-center gap-1">
                    Walmart
                    {resolveListingUrl(product, algopixData, 'walmart') && (
                      <a
                        href={resolveListingUrl(product, algopixData, 'walmart') ?? undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent hover:text-accent-hover"
                        title="View listing"
                      >
                        <ExternalLink size={10} />
                      </a>
                    )}
                  </span>
                </td>
                <td className="px-2 py-1.5 font-mono">{fmt$(product.walmart_price)}</td>
                <td className="px-2 py-1.5 text-ink-muted" colSpan={5}>
                  —
                </td>
              </tr>
            </tbody>
          </table>
        </Section>

        {/* Day 90 (CF 60) — Marketplace Permissions section per A8 amendment +
            #DBN-7 (isAdmin gate; engine-relevant policy class). Button-toggle
            JSX mirrors Directory.tsx BrandForm L3015-L3043 verbatim. Optimistic
            local-mutation pattern: setPermsDraft (optimistic) → supabase UPDATE
            → revert on error. Section hidden entirely for non-admin (no
            read-only display — engine-relevant policy class per Architecture
            Rule 4; matches Settings cluster pattern Day 86/87/89). */}
        {isAdmin && (
          <Section title="Marketplace Permissions">
            <div role="group" aria-label="Marketplace permissions">
              <p className="text-xs text-ink-muted mb-2">
                Per-channel policy at product tier. Explicit deny terminates the resolution chain
                (product → brand → distributor → default allow). Missing key defers to brand.
                Decision 46.
              </p>
              {marketplaces.length === 0 ? (
                <p className="text-2xs text-ink-muted">No marketplaces configured.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {marketplaces.map((m) => {
                    const checked = !!permsDraft[m.channel];
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => togglePerm(m.channel)}
                        aria-pressed={checked}
                        className={clsx(
                          'text-2xs px-2 py-0.5 rounded border flex items-center gap-1',
                          checked
                            ? 'bg-accent/15 text-accent border-accent/40'
                            : 'bg-surface-3 text-ink-muted border-border-subtle'
                        )}
                      >
                        {checked ? <Check size={10} /> : null}
                        {m.display_name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Section 6 — Profitability Summary */}
        <Section title="Profitability Summary">
          {profitability.length > 0 ? (
            <>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-ink-muted border-b border-border-subtle">
                    <th className="px-2 py-1.5 text-left font-medium">Channel</th>
                    {/* Day 84 P6.3 L11 — Sale Price + Velocity columns inserted
                        between Channel and Profit/Unit. Cells render the
                        effective value via per-path override lookup +
                        ProvenanceTooltip mode='inline' badge when the source
                        is the override row. */}
                    <th className="px-2 py-1.5 text-left font-medium">Sale Price</th>
                    <th className="px-2 py-1.5 text-left font-medium">Velocity</th>
                    <th className="px-2 py-1.5 text-left font-medium">Profit/Unit</th>
                    <th className="px-2 py-1.5 text-left font-medium">ROI%</th>
                    <th className="px-2 py-1.5 text-left font-medium">Profit/Day</th>
                    <th className="px-2 py-1.5 text-left font-medium">ROI/Day</th>
                    <th className="px-2 py-1.5 text-left font-medium">Floor</th>
                    <th className="px-2 py-1.5 text-left font-medium">Status</th>
                    <th className="px-2 py-1.5 text-left font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {profitability.map((row) => {
                    // row.marketplace / o.marketplace — pp + override tables' enum-typed
                    // column (values are fpc path keys; the stored name renames only when
                    // the enum dies, slice-B family).
                    const priceOvr = priceOverrides.find((o) => o.marketplace === row.marketplace);
                    const velOvr = velocityOverrides.find((o) => o.marketplace === row.marketplace);
                    const effectiveSalePrice = priceOvr?.sale_price ?? row.sale_price ?? null;
                    const effectiveVelocity = velOvr?.monthly_sales ?? row.monthly_sales ?? null;
                    const priceIsOverride = priceOvr != null;
                    const velocityIsOverride = velOvr != null;
                    return (
                      <tr key={row.marketplace} className="border-b border-border-subtle">
                        <td className="px-2 py-1.5 font-medium text-ink-primary">
                          {PATH_LABELS[row.marketplace] || row.marketplace}
                        </td>
                        <td className="px-2 py-1.5 font-mono">
                          {priceIsOverride ? (
                            <ProvenanceTooltip
                              provenance={priceOvr.provenance as ProvenanceMap | null | undefined}
                              fieldKey="sale_price"
                              staleThresholdDays={staleDays}
                              mode="inline"
                              previousValueFormatter={(v) => fmt$(typeof v === 'number' ? v : null)}
                            >
                              <span className="italic text-ink-bright">
                                {fmt$(effectiveSalePrice)}
                              </span>
                            </ProvenanceTooltip>
                          ) : (
                            <span className="text-ink-secondary">{fmt$(effectiveSalePrice)}</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 font-mono">
                          {velocityIsOverride ? (
                            <ProvenanceTooltip
                              provenance={velOvr.provenance as ProvenanceMap | null | undefined}
                              fieldKey="monthly_sales"
                              staleThresholdDays={staleDays}
                              mode="inline"
                              previousValueFormatter={(v) =>
                                typeof v === 'number' ? String(v) : '—'
                              }
                            >
                              <span className="italic text-ink-bright">
                                {effectiveVelocity != null ? effectiveVelocity : '—'}
                              </span>
                            </ProvenanceTooltip>
                          ) : (
                            <span className="text-ink-secondary">
                              {effectiveVelocity != null ? effectiveVelocity : '—'}
                            </span>
                          )}
                        </td>
                        <td className={clsx('px-2 py-1.5 font-mono', profitColor(row))}>
                          {fmt$(row.profit_per_unit)}
                        </td>
                        <td className={clsx('px-2 py-1.5 font-mono', profitColor(row))}>
                          {fmtPct(row.roi_pct)}
                        </td>
                        <td className="px-2 py-1.5 font-mono">{fmt$(row.profit_per_day_safety)}</td>
                        <td className="px-2 py-1.5 font-mono">{fmtPct(row.roi_per_day_safety)}</td>
                        <td className="px-2 py-1.5 font-mono text-ink-secondary">
                          {fmt$(row.effective_floor)}
                        </td>
                        <td className="px-2 py-1.5">
                          {row.no_price_data ? (
                            <span className="px-1.5 py-0.5 rounded text-2xs bg-surface-4 text-ink-muted">
                              No price
                            </span>
                          ) : row.meets_min_roi ? (
                            <span className="px-1.5 py-0.5 rounded text-2xs bg-success/15 text-success">
                              Meets ROI
                            </span>
                          ) : row.is_profitable ? (
                            <span className="px-1.5 py-0.5 rounded text-2xs bg-warning/15 text-warning">
                              Profitable
                            </span>
                          ) : (
                            <span className="px-1.5 py-0.5 rounded text-2xs bg-danger/15 text-danger">
                              No profit
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          {!isDistRep && (
                            <button
                              type="button"
                              onClick={() => setOverrideModalPath(row.marketplace)}
                              className="text-2xs text-ink-muted hover:text-ink-bright"
                            >
                              Edit overrides
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Cost breakdown for best channel */}
              {(() => {
                const best =
                  profitability.find((r) => !r.no_price_data && r.is_profitable) ||
                  profitability.find((r) => !r.no_price_data) ||
                  profitability[0];
                if (!best || best.no_price_data) return null;
                // best.marketplace — pp's enum-typed stored column; the literals are
                // fpc path keys already (stored name renames at the enum's death, slice B).
                const isFBA = best.marketplace === 'amazon_fba';
                const isFBM = best.marketplace === 'amazon_fbm';
                type Line = {
                  label: string;
                  value: number | null | undefined;
                  bold?: boolean;
                  color?: boolean;
                };
                const lines: Line[] = [
                  {
                    label: 'Product Cost',
                    // product.cost_usd is junction-assembled at useCatalog (survives the
                    // slice-B drop); marketplace_fees is a pp money column (its own name).
                    value:
                      best.total_cost_per_unit != null
                        ? best.total_cost_per_unit - (best.marketplace_fees || 0)
                        : product.cost_usd,
                  },
                  ...(best.marketplace_fees != null
                    ? [
                        {
                          label: isFBA ? 'FBA Fee' : isFBM ? 'FBM Shipping' : 'Channel Fee',
                          value: best.marketplace_fees,
                        },
                      ]
                    : []),
                  ...(best.referral_fee != null
                    ? [{ label: 'Referral Fee', value: best.referral_fee }]
                    : []),
                  ...(best.fba_fee != null && isFBA
                    ? [{ label: 'FBA Fulfillment', value: best.fba_fee }]
                    : []),
                  ...(best.returns_reserve != null
                    ? [{ label: 'Returns Reserve', value: best.returns_reserve }]
                    : []),
                  { label: 'Total Cost Per Unit', value: best.total_cost_per_unit, bold: true },
                  { label: 'Sale Price', value: best.sale_price },
                  {
                    label: 'Profit Per Unit',
                    value: best.profit_per_unit,
                    bold: true,
                    color: true,
                  },
                ];
                return (
                  <div className="mt-4 pt-4 border-t border-border-subtle">
                    <h4 className="text-xs font-medium text-ink-secondary mb-3">
                      Cost Breakdown — {PATH_LABELS[best.marketplace] || best.marketplace}
                    </h4>
                    <div className="space-y-1.5 max-w-sm">
                      {lines.map(({ label, value, bold, color }) => (
                        <div key={label} className="flex items-center justify-between">
                          <span
                            className={clsx(
                              'text-xs',
                              bold ? 'font-medium text-ink-bright' : 'text-ink-secondary'
                            )}
                          >
                            {label}
                          </span>
                          <span
                            className={clsx(
                              'text-xs font-mono',
                              color
                                ? best.is_profitable
                                  ? 'text-success font-semibold'
                                  : 'text-danger font-semibold'
                                : bold
                                  ? 'text-ink-bright'
                                  : 'text-ink-primary'
                            )}
                          >
                            {fmt$(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                    {best.amazon_fee_source && (
                      <p className="text-2xs text-ink-muted mt-2 max-w-sm">
                        Amazon fees: {sourcePrefix(best.amazon_fee_source)}
                      </p>
                    )}
                  </div>
                );
              })()}
            </>
          ) : (
            <div className="text-center py-6">
              <p className="text-xs text-ink-muted mb-2">
                Run a buying session to see profitability for this product
              </p>
              <button
                onClick={() => navigate('/purchasing/buying-session')}
                className="arbi-btn-primary text-xs px-3 py-1.5"
              >
                Go to Buying Session
              </button>
            </div>
          )}
        </Section>

        {/* Section 7 — Algopix Raw Data */}
        {/* Day 83 P6.2 — algopix_data section-level provenance wrap (R3 Sol-lock).
            suppressStale={true} because Algopix data has its own 3-day
            data_pulled_at staleness semantic (migration L104-105 comment), not
            the 90-day manual-override threshold. Tooltip body still renders
            source-prefix + relative timestamp. */}
        <Section
          title="Algopix Raw Data"
          defaultOpen={false}
          action={
            <ProvenanceTooltip
              provenance={effectiveProvenance}
              fieldKey="algopix_data"
              staleThresholdDays={staleDays}
              mode="inline"
              suppressStale={true}
            >
              <Info size={12} className="text-ink-muted" aria-label="Provenance info" />
            </ProvenanceTooltip>
          }
        >
          <div className="max-h-96 overflow-y-auto space-y-0">
            {Object.entries(algopixData)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([key, val]) => (
                <div key={key} className="flex items-start py-1 border-b border-border-subtle">
                  <span
                    className="w-56 flex-shrink-0 text-2xs text-ink-muted font-mono truncate"
                    title={key}
                  >
                    {key}
                  </span>
                  <span className="text-2xs text-ink-primary font-mono break-all">
                    {val === null || val === undefined
                      ? '—'
                      : typeof val === 'object'
                        ? JSON.stringify(val)
                        : String(val)}
                  </span>
                </div>
              ))}
            {Object.keys(algopixData).length === 0 && (
              <p className="text-xs text-ink-muted text-center py-4">No Algopix data available</p>
            )}
          </div>
        </Section>

        {/* Section 8 — Active Flags */}
        <Section
          title="Active Flags"
          icon={Flag}
          action={
            <button
              onClick={handleRescan}
              disabled={rescanning}
              className="arbi-btn-ghost text-xs px-2 py-1"
              title="Re-scan for flags"
            >
              <RefreshCw size={12} className={rescanning ? 'animate-spin' : ''} /> Re-scan
            </button>
          }
        >
          {openFlags.length > 0 ? (
            <div className="space-y-0">
              <div className="mb-3">
                <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-danger/15 text-danger">
                  {openFlags.length} active flag{openFlags.length !== 1 ? 's' : ''}
                </span>
              </div>
              {openFlags.map((flag) => (
                <div
                  key={flag.id}
                  className="flex items-center gap-3 py-2 border-b border-border-subtle"
                >
                  <span
                    className={clsx(
                      'px-1.5 py-0.5 rounded text-2xs font-medium flex-shrink-0',
                      (flag.flag_category && CATEGORY_COLORS[flag.flag_category]) ||
                        'bg-surface-4 text-ink-muted'
                    )}
                  >
                    {(flag.flag_category && CATEGORY_LABELS[flag.flag_category]) ||
                      flag.flag_category}
                  </span>
                  <span className="flex-1 text-xs text-ink-secondary">{flag.flag_reason}</span>
                  <span className="text-2xs text-ink-muted font-mono flex-shrink-0">
                    {relativeTime(flag.created_at)}
                  </span>
                  <button
                    onClick={() => navigate(`/catalog/flagged?product=${product.isg_sku}`)}
                    className="px-2 py-0.5 rounded text-2xs font-medium bg-surface-4 text-ink-secondary hover:text-ink-primary transition-colors cursor-pointer"
                  >
                    Resolve
                  </button>
                  <button
                    onClick={() => setDismissConfirm(flag.id)}
                    className="px-2 py-0.5 rounded text-2xs font-medium bg-surface-4 text-ink-muted hover:text-danger transition-colors cursor-pointer"
                  >
                    Dismiss
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-2 py-4 justify-center">
              <CheckCircle size={20} className="text-success" />
              <span className="text-xs text-success">No active flags</span>
            </div>
          )}
        </Section>

        {/* W4a — the verification gate (Moment 1): confirm a candidate (→ products.asin + reject the
            rest) or reject all (→ no_amazon_listing). Read-only for viewer; dist_rep never sees
            candidates (product_candidates RLS). Renders nothing when there are none. */}
        {matchCandidates.length > 0 && (
          <Section
            title="Match Candidates"
            action={
              canConfirm ? (
                <button
                  onClick={() => setRejectAllConfirm(true)}
                  disabled={gateBusy}
                  className="arbi-btn-ghost text-xs px-2 py-1 text-danger"
                  title="None of these match"
                >
                  <X size={12} /> None match
                </button>
              ) : undefined
            }
          >
            <div className="flex items-start gap-3 mb-3">
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt="Supplier product"
                  className="w-12 h-12 flex-shrink-0 rounded object-contain bg-surface-3 border border-border-subtle"
                />
              )}
              <p className="flex-1 text-2xs text-ink-muted leading-relaxed">
                {canConfirm
                  ? 'Confirm the correct match (sets the product ASIN + rejects the rest) or reject all. Open each ASIN to compare the live listing against the supplier image.'
                  : 'Read-only — confirming a match requires an admin or user role.'}
                {candidateClusterCue && (
                  <span className="mt-1 flex items-center gap-1 text-warning">
                    <AlertTriangle size={11} className="flex-shrink-0" /> Close scores — rank alone
                    isn&apos;t decisive; verify by image/listing.
                  </span>
                )}
              </p>
            </div>
            <div className="space-y-0">
              {visibleCandidates.map((c) => {
                const isFallback = isRecallFallback(c.source);
                const lowConfidence = c.verdict === 'hold' || c.verdict === 'suspect';
                return (
                  <div
                    key={c.id}
                    className="flex items-center gap-2 py-2 border-b border-border-subtle"
                  >
                    <span className="w-6 flex-shrink-0 text-2xs text-ink-muted font-mono">
                      #{c.rank ?? '–'}
                    </span>
                    <a
                      href={`https://www.amazon.com/dp/${c.candidate_asin}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-28 flex-shrink-0 text-xs font-mono text-accent hover:underline inline-flex items-center gap-1"
                      title="Open the Amazon listing to compare"
                    >
                      {c.candidate_asin}
                      <ExternalLink size={10} className="flex-shrink-0" />
                    </a>
                    <span className="flex-1 text-xs text-ink-secondary truncate">
                      {c.title ?? '—'}
                    </span>
                    {isFallback && (
                      <span
                        className="flex-shrink-0 px-1.5 py-0.5 rounded text-2xs bg-surface-4 text-ink-muted"
                        title="Recovered via a broadened search — lower precision, verify carefully"
                      >
                        broad match
                      </span>
                    )}
                    {lowConfidence && (
                      <span
                        className="flex-shrink-0 text-warning"
                        title={`Low confidence — ${c.verdict}`}
                      >
                        {c.verdict === 'hold' ? (
                          <AlertCircle size={12} />
                        ) : (
                          <AlertTriangle size={12} />
                        )}
                      </span>
                    )}
                    <span className="w-9 flex-shrink-0 text-right text-xs text-ink-primary tabular-nums">
                      {c.arbi_confidence_score ?? '—'}
                    </span>
                    <span
                      className={clsx(
                        'px-1.5 py-0.5 rounded text-2xs font-medium flex-shrink-0 text-center',
                        (c.verdict && VERDICT_COLORS[c.verdict]) || 'bg-surface-4 text-ink-muted'
                      )}
                    >
                      {c.verdict ?? '—'}
                    </span>
                    {canConfirm && (
                      <button
                        onClick={() =>
                          setPromoteConfirm({ candidateId: c.id, asin: c.candidate_asin })
                        }
                        disabled={gateBusy}
                        className="arbi-btn-primary text-2xs px-2 py-0.5 flex-shrink-0"
                      >
                        Confirm
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {matchCandidates.length > CANDIDATE_RENDER_CAP && (
              <button
                onClick={() => setShowAllCandidates((s) => !s)}
                className="arbi-btn-ghost text-2xs px-2 py-1 mt-2"
              >
                {showAllCandidates
                  ? 'Show fewer'
                  : `Show ${matchCandidates.length - CANDIDATE_RENDER_CAP} more`}
              </button>
            )}
          </Section>
        )}

        {/* Section 9 — Buying History */}
        <Section title="Buying History">
          <div className="text-center py-6">
            <p className="text-xs text-ink-muted">No purchase history yet</p>
          </div>
        </Section>

        {/* Dismiss flag confirm */}
        <ConfirmDialog
          open={!!dismissConfirm}
          title="Dismiss Flag"
          message="This will dismiss the flag. The product will not appear in the flagged queue unless re-scanned."
          onConfirm={() => {
            if (dismissConfirm) dismissFlag(dismissConfirm);
            setDismissConfirm(null);
          }}
          onCancel={() => setDismissConfirm(null)}
        />

        {/* Remove link confirm */}
        <ConfirmDialog
          open={!!removeLinkConfirm}
          title="Remove this listing link?"
          message="The link will be deactivated. You can add it back at any time."
          onConfirm={() => removeLinkConfirm && handleRemoveLink(removeLinkConfirm)}
          onCancel={() => setRemoveLinkConfirm(null)}
        />

        {/* W4a — confirm a match candidate (Moment 1). Sets products.asin + rejects the rest;
            stamps match_reviewed so the product clears the PO-submit gate (Moment 2). */}
        <ConfirmDialog
          open={!!promoteConfirm}
          title="Confirm this match?"
          message={
            promoteConfirm
              ? `Sets ${product?.isg_sku ?? 'this product'}'s ASIN to ${promoteConfirm.asin} and rejects the other candidates for this product. This makes it buyable.`
              : ''
          }
          confirmLabel="Confirm match"
          onConfirm={() => {
            if (promoteConfirm) void handlePromote(promoteConfirm.candidateId);
            setPromoteConfirm(null);
          }}
          onCancel={() => setPromoteConfirm(null)}
        />

        {/* W4a — reject all candidates (Moment 1). Flags no Amazon listing; closes the resolver loop. */}
        <ConfirmDialog
          open={rejectAllConfirm}
          title="None of these match?"
          message={`Rejects all candidates and flags ${product?.isg_sku ?? 'this product'} as having no Amazon listing.`}
          confirmLabel="Reject all"
          onConfirm={() => {
            void handleRejectAll();
            setRejectAllConfirm(false);
          }}
          onCancel={() => setRejectAllConfirm(false)}
        />

        {/* Day 84 P6.3 L11 — OverrideEditModal mounts when a Section 6 row's
            "Edit overrides" button sets the target path. Modal handles
            INSERT / UPDATE / DELETE per-field with ConfirmDialog-gated
            removal; refreshOverrides re-reads both override tables + the
            profitability cache (cache stale until next engine recalc — UI
            consumes override rows directly via per-path lookup). */}
        {overrideModalPath && product?.id && (
          <OverrideEditModal
            open={!!overrideModalPath}
            productId={product.id}
            path={overrideModalPath}
            pathLabel={PATH_LABELS[overrideModalPath] ?? overrideModalPath}
            existingVelocity={
              velocityOverrides.find((o) => o.marketplace === overrideModalPath) ?? null
            }
            existingPrice={priceOverrides.find((o) => o.marketplace === overrideModalPath) ?? null}
            userId={user?.id ?? null}
            onClose={() => setOverrideModalPath(null)}
            onSaved={refreshOverrides}
            showToast={showToast}
          />
        )}
      </div>
    </div>
  );
}
